import type { AssessmentResponse, AssessmentStatus, DesignationTrafficLight } from '@/lib/api/assessment';
import {
  assessmentResultTone,
  assessmentStatusTitle,
  type AssessmentResultTone,
} from '@/lib/assessment-result';
import { displaySummaryHe, trafficLightMeaning } from '@/lib/assessment-display';
import styles from './AssessmentResultBanner.module.css';

export type AssessmentResultBannerLabels = {
  status: Record<AssessmentStatus, string>;
  trafficLight: Record<DesignationTrafficLight, string>;
  unknownNote: string;
};

type Props = {
  assessment: AssessmentResponse;
  labels: AssessmentResultBannerLabels;
  /** Short heading above the status (e.g. on planning-info page). */
  sectionLabel?: string;
  variant?: 'default' | 'compact';
  showSummary?: boolean;
  className?: string;
  'data-testid'?: string;
};

function toneClass(tone: AssessmentResultTone): string {
  switch (tone) {
    case 'green':
      return styles.toneGreen;
    case 'yellow':
      return styles.toneYellow;
    case 'red':
      return styles.toneRed;
    default:
      return styles.toneUnknown;
  }
}

export default function AssessmentResultBanner({
  assessment,
  labels,
  sectionLabel,
  variant = 'default',
  showSummary = true,
  className,
  'data-testid': testId = 'assessment-status-box',
}: Props) {
  const tone = assessmentResultTone(assessment.status, assessment.traffic_light);
  const meaning = trafficLightMeaning(labels.trafficLight[assessment.traffic_light]);
  const summary = displaySummaryHe(assessment.summary_he);
  const showMeaning = assessment.traffic_light !== 'unknown' && meaning.length > 0;
  const showSummaryText =
    showSummary &&
    assessment.status !== 'unknown' &&
    summary.length > 0 &&
    summary !== meaning;

  return (
    <section
      className={[
        styles.banner,
        variant === 'compact' ? styles.compact : '',
        toneClass(tone),
        className,
      ]
        .filter(Boolean)
        .join(' ')}
      aria-live="polite"
      data-testid={testId}
    >
      {sectionLabel ? <p className={styles.label}>{sectionLabel}</p> : null}

      <div className={styles.top}>
        <span className={styles.signalDot} aria-hidden />
        <h2 className={styles.title}>
          {assessmentStatusTitle(assessment.status, labels.status)}
        </h2>
      </div>

      {showMeaning ? (
        <p className={styles.meaning} data-testid="assessment-traffic-meaning">
          {meaning}
        </p>
      ) : null}

      {assessment.designation_matched ? (
        <p className={styles.designation} data-testid="assessment-designation">
          {assessment.designation_matched}
        </p>
      ) : null}

      {assessment.status === 'unknown' ? (
        <p className={styles.summary}>{labels.unknownNote}</p>
      ) : showSummaryText ? (
        <p className={styles.summary} data-testid="assessment-summary">
          {summary}
        </p>
      ) : null}
    </section>
  );
}
